import { Injectable } from '@angular/core';

import { Observable, of } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';

import { ApiFragment, Family, ProfileDataService, ReferenceCodes, ReferenceTitles } from '@stylehaven/components';
import { ClassificationService } from '../api/classification.service';
import { MiddlewareService } from './middleware.abstract.service';

@Injectable({ providedIn: 'root' })
export class ProfileMiddleware implements MiddlewareService {
    constructor(
        private profileService: ProfileDataService,
        private classificationService: ClassificationService
    ) {}

    public annotate(apiRecord: ApiFragment): Observable<ApiFragment> {
        return this.appendProfilesIfNoClassification(apiRecord).pipe(
            switchMap(record => this.appendClassificationTitle(record)),
            // MED-984: return original if annotation fails (due to classification not reachable)
            catchError(error => of(apiRecord)),
            take(1)
        );
    }

    private appendProfilesIfNoClassification(record: ApiFragment): Observable<ApiFragment> {
        /* 2021-apr-30 Current Rest-API v2 status
         *
         * When linking a Record to a Classification directly, the Profiles are present in the Context block
         * When linking a Record to a Collection that is linked to a Classification,
         * the Profiles from the classification will be present in the Context block.
         *
         * Records that do not have a linked classification (ex. Legacy MH records)
         * will receive the fallback default classification (if it's not a candidate child)
         *  and the corresponding Profiles below.
         * */

        if (record.getClassificationId() !== null) {
            return of(record);
        }

        return this.appendFallbackClassification(record).pipe(switchMap(record => this.appendFallbackProfiles(record)));
    }

    private appendFallbackClassification(record: ApiFragment): Observable<ApiFragment> {
        if (record.isCandidateChild()) {
            return of(record);
        }

        return this.classificationService.getFallbackClassification().pipe(
            take(1),
            map(classification => classification.getMediaObjectId()),
            map(classificationRefeferenceCode => {
                const referenceCodes: ReferenceCodes = record.get(Family.Structural, 'ReferenceCodes');
                const updated: ReferenceCodes = {
                    ...referenceCodes,
                    Classification: [classificationRefeferenceCode]
                };
                record.set(Family.Structural, 'ReferenceCodes', updated);
                return record;
            })
        );
    }

    private appendFallbackProfiles(record: ApiFragment): Observable<ApiFragment> {
        return this.classificationService.getFallbackClassificationProfileIds().pipe(
            switchMap(profileIds =>
                this.profileService.filterProfilesIdsForRecordType(profileIds, record.getRecordType())
            ),
            map(defaultProfileIds => {
                record.set(Family.Context, 'Profiles', defaultProfileIds);
                return record;
            })
        );
    }

    private appendClassificationTitle(fragment: ApiFragment): Observable<ApiFragment> {
        /* MED-984: Block users from adding items to classifications
         * User should only be able to add items to a classifications if they have view rights to that classification.
         * This means that they will not be able to fetch the title of this classification.
         *
         * Since ENG-2138 was implemented Classification ReferenceTitles are automatically added for Records linked to a classification.
         *
         * Records that do not have a linked classification (ex. Legacy MH records)
         * will receive the fallback default classification and the corresponding ReferenceTitle will be added below.
         *
         * */

        return of(fragment.getClassificationId()).pipe(
            switchMap(classificationReferenceCode => {
                if (classificationReferenceCode !== null && fragment.getClassificationTitle() === null) {
                    return this.classificationService.get(classificationReferenceCode);
                } else {
                    return of(undefined);
                }
            }),
            map((classification: ApiFragment | undefined) => {
                if (!classification) {
                    return fragment;
                }
                const referenceTitles: ReferenceTitles = fragment.get(Family.Structural, 'ReferenceTitles');
                const updated: ReferenceTitles = {
                    ...referenceTitles,
                    Classification: [classification?.getTitle()]
                };

                fragment.set(Family.Structural, 'ReferenceTitles', updated);
                return fragment;
            })
        );
    }
}
